import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Edit, Trash2, Loader2, Users, Search, X, GitMerge, Megaphone } from 'lucide-react';
import { getListCache, setListCache, clearListCache } from '../lib/listCache';

export default function GroupsPage() {
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  // Cache stale-while-revalidate: reentrar na tela mostra os grupos na hora (sem loader) e revalida atrás.
  const _initCache = getListCache('groups');
  const [groups, setGroups] = useState(_initCache?.value || []);
  const [loading, setLoading] = useState(!_initCache);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const percentTimerRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // Mesclar grupos
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeSources, setMergeSources] = useState([]);
  const [merging, setMerging] = useState(false);

  // Efeito para animação de loading
  useEffect(() => {
    if (loading) {
      setLoadingPercent(0);
      percentTimerRef.current = setInterval(() => {
        setLoadingPercent(prev => {
          if (prev >= 95) return 95;
          const increment = Math.max(0.5, (95 - prev) / 20);
          return Math.min(95, prev + increment);
        });
      }, 100);
    } else {
      setLoadingPercent(100);
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
        percentTimerRef.current = null;
      }
    }
    return () => {
      if (percentTimerRef.current) clearInterval(percentTimerRef.current);
    };
  }, [loading]);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async ({ background = false } = {}) => {
    if (!background) {
      const cached = getListCache('groups');
      if (cached) {
        setGroups(cached.value);
        setLoading(false);
        fetchGroups({ background: true }); // revalida em segundo plano (sem loader)
        return;
      }
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Ordenar por nome alfabeticamente
      const sortedByName = data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
      setGroups(sortedByName);
      setListCache('groups', sortedByName);
    } catch (error) {
      if (!background) toast.error('Erro ao carregar grupos');
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupName(group.name);
    } else {
      setEditingGroup(null);
      setGroupName('');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingGroup
        ? `${process.env.REACT_APP_BACKEND_URL}/api/groups/${editingGroup._id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/groups`;
      
      const response = await fetch(url, {
        method: editingGroup ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: groupName.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao salvar grupo');
      }

      toast.success(editingGroup ? 'Grupo atualizado' : 'Grupo criado');
      setDialogOpen(false);
      clearListCache(); // mutação → invalida caches de lista (grupos + contatos)
      fetchGroups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${groupToDelete._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao excluir grupo');

      toast.success('Grupo excluído');
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      clearListCache();
      fetchGroups();
    } catch (error) {
      toast.error('Erro ao excluir grupo');
    }
  };

  // Filtrar grupos
  const filteredGroups = groups.filter((group) => {
    // Filtro por nome
    if (nameFilter && !group.name.toLowerCase().includes(nameFilter.toLowerCase())) {
      return false;
    }
    // Filtro por data inicial
    if (dateFromFilter) {
      const groupDate = new Date(group.created_at).setHours(0, 0, 0, 0);
      const filterDate = new Date(dateFromFilter).setHours(0, 0, 0, 0);
      if (groupDate < filterDate) return false;
    }
    // Filtro por data final
    if (dateToFilter) {
      const groupDate = new Date(group.created_at).setHours(0, 0, 0, 0);
      const filterDate = new Date(dateToFilter).setHours(0, 0, 0, 0);
      if (groupDate > filterDate) return false;
    }
    return true;
  });

  const handleClearFilters = () => {
    setNameFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const openMergeDialog = () => {
    setMergeTarget('');
    setMergeSources([]);
    setMergeDialogOpen(true);
  };

  const toggleMergeSource = (id) => {
    setMergeSources((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const confirmMerge = async () => {
    if (!mergeTarget || mergeSources.length === 0) return;
    setMerging(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/merge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_ids: mergeSources, target_id: mergeTarget }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao mesclar grupos');
      }
      const result = await response.json();
      toast.success(`${result.merged} grupo(s) mesclado(s) · ${result.contacts} contato(s) movido(s)`);
      setMergeDialogOpen(false);
      clearListCache();
      fetchGroups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="sticky top-0 z-20 bg-background pb-4">
        <div className="flex items-center gap-3">
          {/* Name Filter */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Nome do grupo..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-9"
              data-testid="groups-name-filter"
            />
          </div>

          {/* Date From Filter */}
          <div className="flex-1">
            <Input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              data-testid="groups-date-from-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Date To Filter */}
          <div className="flex-1">
            <Input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              data-testid="groups-date-to-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Clear Button */}
          <Button 
            variant="secondary" 
            onClick={handleClearFilters}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>

          {/* Merge Button */}
          {canEdit && groups.length >= 2 && (
            <Button
              variant="secondary"
              onClick={openMergeDialog}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
              data-testid="groups-merge-button"
            >
              <GitMerge className="w-4 h-4 mr-2" />
              Mesclar
            </Button>
          )}

          {/* New Group Button */}
          {canEdit && (
            <Button onClick={() => handleOpenDialog()} data-testid="groups-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Grupo
            </Button>
          )}
        </div>
      </div>

      {/* Loading State - Centralizado */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 p-8">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {Math.round(loadingPercent)}%
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-center">Carregando grupos...</p>
          </div>
        </div>
      ) : (
      <>
      {/* Table */}
      <Card className="flex-1 flex flex-col min-h-0 mb-4">
        <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          <Table data-testid="groups-table">
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Contatos</TableHead>
                <TableHead>Criado em</TableHead>
                {canEdit && <TableHead className="w-[100px]">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 4 : 3} className="text-center py-8">
                    <div className="empty-state">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {groups.length === 0 ? 'Nenhum grupo criado ainda' : 'Nenhum grupo encontrado com os filtros aplicados'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group) => (
                  <TableRow key={group._id} className="hover:bg-primary/5">
                    <TableCell className="font-medium py-2">{group.name}</TableCell>
                    <TableCell className="py-2">
                      {group.contact_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/contacts?group=${group._id}`)}
                          className="flex items-center gap-2 text-primary hover:underline underline-offset-2"
                          data-testid={`group-view-contacts-${group._id}`}
                          title="Ver contatos deste grupo"
                        >
                          <Users className="w-4 h-4" />
                          <span>{group.contact_count}</span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          <span>0</span>
                          <Badge variant="outline" className="border-transparent shadow bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">vazio</Badge>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2">
                      {new Date(group.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="py-2">
                        <div className="flex gap-1">
                          {group.contact_count > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/campaigns?group=${group._id}`)}
                              className="h-7 w-7"
                              title="Criar campanha para este grupo"
                              data-testid={`group-campaign-${group._id}`}
                            >
                              <Megaphone />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(group)}
                            className="h-7 w-7"
                            title="Editar grupo"
                          >
                            <Edit />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(group)}
                            className="h-7 w-7"
                            title="Excluir grupo"
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Editar Grupo' : 'Novo Grupo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Grupo</Label>
                <Input
                  id="name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Ex: Clientes VIP"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir o grupo <strong>{groupToDelete?.name}</strong>?
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Os contatos não serão excluídos.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mesclar grupos</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Grupo de destino (será mantido)</Label>
              <Select
                value={mergeTarget}
                onValueChange={(v) => {
                  setMergeTarget(v);
                  setMergeSources((prev) => prev.filter((id) => id !== v));
                }}
              >
                <SelectTrigger data-testid="merge-target">
                  <SelectValue placeholder="Escolha o grupo que ficará" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g._id} value={g._id}>
                      {g.name} ({g.contact_count || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Grupos a mesclar (serão excluídos)</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                {groups.filter((g) => g._id !== mergeTarget).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum outro grupo disponível.</p>
                ) : (
                  groups
                    .filter((g) => g._id !== mergeTarget)
                    .map((g) => (
                      <div key={g._id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`merge-src-${g._id}`}
                          checked={mergeSources.includes(g._id)}
                          onCheckedChange={() => toggleMergeSource(g._id)}
                        />
                        <Label htmlFor={`merge-src-${g._id}`} className="cursor-pointer font-normal">
                          {g.name} <span className="text-muted-foreground">({g.contact_count || 0})</span>
                        </Label>
                      </div>
                    ))
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Os contatos dos grupos marcados serão movidos para <strong>o grupo de destino</strong>, e esses grupos
              serão excluídos. Os contatos <strong>não</strong> são apagados.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmMerge}
              disabled={merging || !mergeTarget || mergeSources.length === 0}
              data-testid="merge-confirm"
            >
              {merging && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Mesclar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
